import {
  IEnrollmentsService,
  EnrollmentsPaginatedResponse,
  SoftDeleteResponse,
  EnrichedEnrollment,
  EnrollmentSearchKey,
} from './IEnrollmentsService';
import {
  EnrollmentService,
  InitWithAmplify,
  InitEnvironmentVariable,
} from '@PikaDoApp/npm-pikado-logic';
import type {
  NewEnrollment,
  NewCreateEnrollmentInput,
  NewUpdateEnrollmentInput,
  NewCreateFreeEnrollmentInput,
  NewCreatedEnrollment,
  NewUpdatedEnrollment,
  NewEnrollmentAfterPlacementTest,
  NewFreeEnrollment,
} from '@PikaDoApp/npm-pikado-logic/dist/modules/enrollment/graphql/enrollment.interface';
import {Amplify} from 'aws-amplify';
import {gql} from '@apollo/client';
import {enrollmentApolloClient} from '@PikaDoApp/npm-pikado-logic/dist/utils/apolloClient';
import {getEnvironment} from '../../lib/getEnvironment';
import {StudentsService} from '../Students/StudentsService';
import {PackagesService} from '../Package/PackagesService';
import {LevelsService} from '../Level/LevelsService';
import {TestsService} from '../Test/TestsService';
import {PlacementTestsService} from '../PlacementTest/PlacementTestsService';
import {GradesService} from '../Grade/GradesService';
import {SchoolService} from '../Schools/SchoolService';

type EnrollmentClassification = 'student' | 'classSchool';

type FindAllEnrollmentsQueryResponse = {
  findAllEnrollments: {
    data: Array<NewEnrollment & {classId?: string | null}>;
    total: number;
    page: number;
    limit: number;
    totalPages: number;
    studentTotal?: number | null;
    classSchoolTotal?: number | null;
  };
};

const findAllEnrollmentsQuery = gql`
  query FindAllEnrollments(
    $page: Int
    $limit: Int
    $classification: String
    $searchKey: String
    $searchValue: String
  ) {
    findAllEnrollments(
      page: $page
      limit: $limit
      classification: $classification
      searchKey: $searchKey
      searchValue: $searchValue
    ) {
      data {
        _id
        studentId
        classId
        startDate
        paymentStatus
        placementStatus
        levelId
        gradeId
        dayShift
        placementTestId
        placementTestAnswerId
        curriculumId
        endDate
        partnerId
        createdBy
        isDeleted
        createdAt
        updatedAt
        enrollmentStatus
        finalPrice {
          price
          currency
        }
        isLoadTest
        planId
        isFree
        paymentMethod
        oldId
        paymentReferenceId
        packageId {
          _id
        }
        discountCodeId {
          _id
        }
      }
      total
      page
      limit
      totalPages
      studentTotal
      classSchoolTotal
    }
  }
`;

export class EnrollmentsService implements IEnrollmentsService {
  enrollmentService: EnrollmentService;
  studentsService: StudentsService;
  packagesService: PackagesService;
  levelsService: LevelsService;
  testsService: TestsService;
  placementTestsService: PlacementTestsService;
  gradesService: GradesService;
  schoolService: SchoolService;
  private classSchoolByIdCache = new Map<string, Promise<any>>();
  private enrollmentsPageCache = new Map<
    string,
    Promise<EnrollmentsPaginatedResponse>
  >();

  constructor() {
    InitWithAmplify(Amplify);
    InitEnvironmentVariable(getEnvironment());
    this.enrollmentService = new EnrollmentService();
    this.studentsService = new StudentsService();
    this.packagesService = new PackagesService();
    this.levelsService = new LevelsService();
    this.testsService = new TestsService();
    this.placementTestsService = new PlacementTestsService();
    this.gradesService = new GradesService();
    this.schoolService = new SchoolService();
  }

  private getClassSchoolId(student: any): string | undefined {
    return (
      student?.classschool?.id ??
      student?.classschool?._id ??
      student?.classSchool?.id ??
      student?.classSchool?._id ??
      student?.classschoolId ??
      student?.classSchoolId
    );
  }

  private async getClassSchoolById(classSchoolId: string): Promise<any> {
    if (!this.classSchoolByIdCache.has(classSchoolId)) {
      this.classSchoolByIdCache.set(
        classSchoolId,
        this.schoolService.getClassSchoolById(classSchoolId).catch(error => {
          this.classSchoolByIdCache.delete(classSchoolId);
          throw error;
        }),
      );
    }

    return await this.classSchoolByIdCache.get(classSchoolId)!;
  }

  private async enrichEnrollment(enrollment: any): Promise<EnrichedEnrollment> {
    let student: any;

    if (enrollment.studentId) {
      try {
        student = await this.studentsService.getStudentById(
          enrollment.studentId,
        );
      } catch (error) {
        console.error(`Error fetching student ${enrollment.studentId}:`, error);
        student = {
          id: enrollment.studentId,
          name: 'Unknown',
        };
      }
    }

    const classSchoolId = enrollment.classId ?? this.getClassSchoolId(student);
    let classSchool = student?.classschool ?? student?.classSchool;

    if (classSchoolId && !classSchool?.name) {
      try {
        classSchool = await this.getClassSchoolById(classSchoolId);
      } catch (error) {
        console.error(`Error fetching class school ${classSchoolId}:`, error);
        classSchool = {
          id: classSchoolId,
          name: classSchoolId,
        };
      }
    }

    return {
      ...enrollment,
      student: student
        ? {
            ...student,
            classschool: classSchool ?? student?.classschool,
          }
        : undefined,
      classSchool: classSchool
        ? {
            ...classSchool,
            id: classSchool.id ?? classSchool._id ?? classSchoolId,
          }
        : undefined,
    };
  }

  async getEnrollments(
    page: number = 1,
    limit: number = 20,
    force = false,
  ): Promise<EnrollmentsPaginatedResponse> {
    return this.fetchEnrollmentsPage(page, limit, undefined, force);
  }

  private async fetchEnrollmentsPage(
    page: number = 1,
    limit: number = 20,
    classification?: EnrollmentClassification,
    force = false,
    searchKey?: EnrollmentSearchKey,
    searchValue?: string,
  ): Promise<EnrollmentsPaginatedResponse> {
    const trimmedSearchValue = searchValue?.trim();
    const cacheKey = [
      classification ?? 'all',
      page,
      limit,
      searchKey ?? 'none',
      trimmedSearchValue ?? '',
    ].join(':');

    if (force) {
      this.enrollmentsPageCache.clear();
    }

    if (!this.enrollmentsPageCache.has(cacheKey)) {
      this.enrollmentsPageCache.set(
        cacheKey,
        this.fetchEnrollmentsPageFromApi(
          page,
          limit,
          classification,
          searchKey,
          trimmedSearchValue,
        ).catch(error => {
          this.enrollmentsPageCache.delete(cacheKey);
          throw error;
        }),
      );
    }

    return await this.enrollmentsPageCache.get(cacheKey)!;
  }

  private async fetchEnrollmentsPageFromApi(
    page: number,
    limit: number,
    classification?: EnrollmentClassification,
    searchKey?: EnrollmentSearchKey,
    searchValue?: string,
  ): Promise<EnrollmentsPaginatedResponse> {
    try {
      const response =
        await enrollmentApolloClient().query<FindAllEnrollmentsQueryResponse>({
          query: findAllEnrollmentsQuery,
          variables: {page, limit, classification, searchKey, searchValue},
          fetchPolicy: 'network-only',
        });

      const enrollmentsData = response.data.findAllEnrollments;

      const enrichedItems = await Promise.all(
        (enrollmentsData.data || []).map((enrollment: any) =>
          this.enrichEnrollment(enrollment),
        ),
      );

      return {
        items: enrichedItems,
        page: enrollmentsData.page,
        totalPages: enrollmentsData.totalPages,
        total: enrollmentsData.total,
        nextToken:
          enrollmentsData.page < enrollmentsData.totalPages
            ? `page-${enrollmentsData.page + 1}`
            : null,
        scannedCount: enrollmentsData.total,
        classificationTotals: {
          students: enrollmentsData.studentTotal ?? 0,
          classSchools: enrollmentsData.classSchoolTotal ?? 0,
        },
      };
    } catch (error) {
      console.error('Error fetching enrollments:', error);
      throw error;
    }
  }

  async getClassSchoolEnrollments(
    page: number = 1,
    limit: number = 20,
    force = false,
    searchValue?: string,
  ): Promise<EnrollmentsPaginatedResponse> {
    return this.fetchEnrollmentsPage(
      page,
      limit,
      'classSchool',
      force,
      searchValue?.trim() ? 'classId' : undefined,
      searchValue,
    );
  }

  async getStudentEnrollments(
    page: number = 1,
    limit: number = 20,
    force = false,
    searchValue?: string,
  ): Promise<EnrollmentsPaginatedResponse> {
    return this.fetchEnrollmentsPage(
      page,
      limit,
      'student',
      force,
      searchValue?.trim() ? 'studentId' : undefined,
      searchValue,
    );
  }

  async getEnrollmentById(enrollmentId: string): Promise<EnrichedEnrollment> {
    try {
      const response =
        await this.enrollmentService.newFindEnrollmentById(enrollmentId);
      console.log('mhhr response->', response);
      const enrollment = response.findEnrollmentById;

      // Enrich enrollment with related entities
      const enrichedEnrollment: EnrichedEnrollment = {...enrollment};

      // Fetch student
      if (enrollment.studentId) {
        try {
          enrichedEnrollment.student =
            await this.studentsService.getStudentById(enrollment.studentId);
        } catch (error) {
          console.error(
            `Error fetching student ${enrollment.studentId}:`,
            error,
          );
          enrichedEnrollment.student = {
            id: enrollment.studentId,
            name: 'Unknown',
          };
        }
      }

      // Fetch package
      if (enrollment.packageId?._id) {
        try {
          const packageData = await this.packagesService.getPackageById(
            enrollment.packageId._id,
          );
          enrichedEnrollment.package = {
            id: packageData._id,
            name:
              packageData.name_i18n?.en ||
              packageData.name_i18n?.ar ||
              'Unknown',
            ...packageData,
          };
        } catch (error) {
          console.error(
            `Error fetching package ${enrollment.packageId._id}:`,
            error,
          );
          enrichedEnrollment.package = {
            id: enrollment.packageId._id,
            name: 'Unknown',
          };
        }
      }

      // Fetch level
      if (enrollment.levelId) {
        try {
          enrichedEnrollment.level = await this.levelsService.getNewLevelById(
            enrollment.levelId,
          );
        } catch (error) {
          console.error(`Error fetching level ${enrollment.levelId}:`, error);
          enrichedEnrollment.level = {
            id: enrollment.levelId,
            name: 'Unknown',
          };
        }
      }

      // Fetch grade
      if (enrollment.gradeId) {
        try {
          enrichedEnrollment.grade = await this.gradesService.getNewGradeById(
            enrollment.gradeId,
          );
        } catch (error) {
          console.error(`Error fetching grade ${enrollment.gradeId}:`, error);
          enrichedEnrollment.grade = {
            id: enrollment.gradeId,
            name: 'Unknown',
          };
        }
      }

      // Fetch placement test
      if (enrollment.placementTestId) {
        try {
          enrichedEnrollment.test =
            await this.placementTestsService.getPlacementTestById(
              enrollment.placementTestId,
            );
        } catch (error) {
          console.error(
            `Error fetching placement test ${enrollment.placementTestId}:`,
            error,
          );
          enrichedEnrollment.test = {
            id: enrollment.placementTestId,
            name: 'Unknown',
          };
        }
      }

      const classSchoolId = this.getClassSchoolId(enrichedEnrollment.student);
      if (classSchoolId) {
        try {
          const classSchool =
            enrichedEnrollment.student?.classschool?.name ||
            enrichedEnrollment.student?.classSchool?.name
              ? (enrichedEnrollment.student.classschool ??
                enrichedEnrollment.student.classSchool)
              : await this.getClassSchoolById(classSchoolId);

          enrichedEnrollment.classSchool = {
            ...classSchool,
            id: classSchool.id ?? classSchool._id ?? classSchoolId,
          };
        } catch (error) {
          console.error(`Error fetching class school ${classSchoolId}:`, error);
        }
      }

      return enrichedEnrollment;
    } catch (error) {
      console.error('Error fetching enrollment by id:', error);
      throw error;
    }
  }

  async getEnrollmentsByStudentId(studentId: string): Promise<NewEnrollment[]> {
    try {
      const response =
        await this.enrollmentService.newFindEnrollmentsByStudentId(studentId);
      return response.findEnrollmentsByStudentId || [];
    } catch (error) {
      console.error('Error fetching enrollments by student id:', error);
      throw error;
    }
  }

  async createEnrollment(
    input: NewCreateEnrollmentInput,
  ): Promise<NewCreatedEnrollment> {
    try {
      console.log('Creating enrollment with input:', input);
      const response = await this.enrollmentService.newCreateEnrollment(input);
      return response.createEnrollment;
    } catch (error: any) {
      console.error(
        'Error creating enrollment:',
        JSON.stringify(error, null, 2),
      );

      if (error.errors) {
        throw new Error(error.errors.map((e: any) => e.message).join(', '));
      }

      throw new Error(error.message || 'Unknown error creating enrollment');
    }
  }

  async updateEnrollment(
    input: NewUpdateEnrollmentInput,
  ): Promise<NewUpdatedEnrollment> {
    try {
      console.log('Updating enrollment with input:', input);
      const response = await this.enrollmentService.newUpdateEnrollment(input);
      return response.updateEnrollment;
    } catch (error: any) {
      console.error('Error updating enrollment:', error);

      if (error?.errors?.length) {
        throw new Error(error.errors.map((e: any) => e.message).join(', '));
      }

      throw new Error(error.message || 'Unknown error updating enrollment');
    }
  }

  async softDeleteEnrollment(
    enrollmentId: string,
  ): Promise<SoftDeleteResponse> {
    try {
      console.log('Soft deleting enrollment:', enrollmentId);
      const response =
        await this.enrollmentService.newSoftDeleteEnrollment(enrollmentId);
      return {
        success: response.softDeleteEnrollment,
        message: response.softDeleteEnrollment
          ? 'Enrollment deleted successfully'
          : 'Failed to delete enrollment',
        enrollmentId,
      };
    } catch (error: any) {
      console.error('Error soft deleting enrollment:', error);
      throw new Error(error.message || 'Unknown error deleting enrollment');
    }
  }

  async createFreeEnrollment(
    input: NewCreateFreeEnrollmentInput,
  ): Promise<NewFreeEnrollment> {
    try {
      console.log('Creating free enrollment with input:', input);
      const response =
        await this.enrollmentService.newCreateFreeEnrollment(input);
      return response.createFreeEnrollment;
    } catch (error: any) {
      console.error('Error creating free enrollment:', error);
      throw new Error(
        error.message || 'Unknown error creating free enrollment',
      );
    }
  }

  async updateEnrollmentAfterPlacementTest(
    enrollmentId: string,
    placementTestAnswerId: string,
  ): Promise<NewEnrollmentAfterPlacementTest> {
    try {
      console.log('Updating enrollment after placement test:', {
        enrollmentId,
        placementTestAnswerId,
      });
      const response =
        await this.enrollmentService.newUpdateEnrollmentAfterPlacementTest(
          enrollmentId,
          placementTestAnswerId,
        );
      return response.updateEnrollmentAfterPlacementTest;
    } catch (error: any) {
      console.error('Error updating enrollment after placement test:', error);
      throw new Error(
        error.message ||
          'Unknown error updating enrollment after placement test',
      );
    }
  }
}
